import { Document, Page, StyleSheet, Text, View } from "@react-pdf/renderer";

const styles = StyleSheet.create({
  page: {
    padding: 28,
    fontFamily: "Helvetica",
    fontSize: 11,
    color: "#1f2530",
  },
  header: {
    borderBottomWidth: 2,
    borderBottomColor: "#25374f",
    borderStyle: "solid",
    paddingBottom: 10,
    marginBottom: 16,
  },
  title: {
    fontSize: 18,
    color: "#25374f",
    fontWeight: "bold",
  },
  metaRow: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 12,
    marginTop: 6,
  },
  metaItem: {
    fontSize: 10,
    color: "#4b5563",
  },
  section: {
    marginBottom: 16,
  },
  sectionTitle: {
    fontSize: 14,
    fontWeight: "bold",
    color: "#25374f",
    marginBottom: 8,
  },
  tableHeader: {
    flexDirection: "row",
    backgroundColor: "#25374f",
    color: "white",
    padding: 8,
    fontWeight: "bold",
    fontSize: 10,
  },
  tableRow: {
    flexDirection: "row",
    paddingVertical: 6,
    paddingHorizontal: 8,
    borderBottomWidth: 1,
    borderBottomColor: "#e5e7eb",
    borderStyle: "solid",
  },
  colFloor: { width: "16%" },
  colArea: { width: "12%" },
  colPersons: { width: "12%" },
  colCount: { width: "12%" },
  colSmall: { width: "9%" },
  muted: {
    color: "#6b7280",
  },
  summaryGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 12,
  },
  summaryBlock: {
    flexBasis: "48%",
    borderWidth: 1,
    borderColor: "#e5e7eb",
    borderStyle: "solid",
    borderRadius: 6,
    padding: 12,
  },
  summaryLabel: {
    color: "#4b5563",
    marginBottom: 4,
  },
  summaryValue: {
    fontWeight: "bold",
    marginBottom: 6,
  },
  footer: {
    position: "absolute",
    bottom: 20,
    left: 28,
    right: 28,
    fontSize: 9,
    color: "#6b7280",
    flexDirection: "row",
    justifyContent: "space-between",
  },
});

const ORD_WORDS = [
  "First",
  "Second",
  "Third",
  "Fourth",
  "Fifth",
  "Sixth",
  "Seventh",
  "Eighth",
  "Ninth",
  "Tenth",
  "Eleventh",
  "Twelfth",
];

function storeyName(floorIndex, entranceIdx) {
  if (entranceIdx == null) return `Floor ${floorIndex + 1}`;
  const offset = floorIndex - entranceIdx;
  if (offset === 0) return "Ground Floor";
  if (offset < 0) {
    const n = Math.abs(offset);
    return n === 1 ? "Basement" : `Basement ${n}`;
  }
  if (offset <= ORD_WORDS.length) return `${ORD_WORDS[offset - 1]} Floor`;
  return `${offset}th Floor`;
}

const formatNumber = (value, digits = 0) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return "—";
  return num.toFixed(digits);
};

const safeCount = (value) => {
  const num = Number(value);
  return Number.isFinite(num) ? num : "—";
};

const accessibleCount = (row) => {
  if (!row) return 0;
  if (typeof row.accessible_wcs === "number") return row.accessible_wcs;
  return row.persons > 0 ? 1 : 0;
};

const findLargestByArea = (rows) => {
  if (!Array.isArray(rows) || rows.length === 0) return { idx: -1, row: null };
  let largest = rows[0];
  let idx = 0;
  rows.forEach((r, i) => {
    if (Number(r?.nia_m2) > Number(largest?.nia_m2)) {
      largest = r;
      idx = i;
    }
  });
  return { idx, row: largest };
};

const MetadataHeader = ({ project }) => {
  const projectName = project?.name || "Pebbledash project";
  const buildingUse = project?.building_use || "Office";
  const location = project?.local_authority || project?.postcode || "Location unknown";
  const dateLabel = new Date().toLocaleDateString();

  return (
    <View style={styles.header}>
      <Text style={styles.title}>Pebbledash WC Provision Report</Text>
      <View style={styles.metaRow}>
        <Text style={styles.metaItem}>Project: {projectName}</Text>
        <Text style={styles.metaItem}>Location: {location}</Text>
        <Text style={styles.metaItem}>Building use: {buildingUse}</Text>
        <Text style={styles.metaItem}>Date: {dateLabel}</Text>
      </View>
    </View>
  );
};

const TableHeader = ({ includeUrinals, includeAccessible }) => (
  <View style={styles.tableHeader}>
    <Text style={styles.colFloor}>Floor</Text>
    <Text style={styles.colArea}>NIA (m²)</Text>
    <Text style={styles.colPersons}>Persons</Text>
    <Text style={styles.colCount}>Male WCs</Text>
    <Text style={[styles.colCount, !includeUrinals && styles.muted]}>Urinals</Text>
    <Text style={styles.colCount}>Female WCs</Text>
    {includeAccessible && <Text style={styles.colCount}>Accessible</Text>}
    <Text style={styles.colSmall}>Male basins</Text>
    <Text style={styles.colSmall}>Female basins</Text>
  </View>
);

const TableRow = ({ row, floorLabel, includeUrinals, includeAccessible }) => {
  const accessible = includeAccessible ? accessibleCount(row) : null;

  return (
    <View style={styles.tableRow}>
      <Text style={styles.colFloor}>{floorLabel}</Text>
      <Text style={styles.colArea}>{formatNumber(row?.nia_m2, 0)}</Text>
      <Text style={styles.colPersons}>{safeCount(row?.persons)}</Text>
      <Text style={styles.colCount}>{safeCount(row?.male_wcs)}</Text>
      <Text style={[styles.colCount, !includeUrinals && styles.muted]}>
        {includeUrinals ? safeCount(row?.urinals) : "—"}
      </Text>
      <Text style={styles.colCount}>{safeCount(row?.female_wcs)}</Text>
      {includeAccessible && <Text style={styles.colCount}>{accessible}</Text>}
      <Text style={styles.colSmall}>{safeCount(row?.male_basins)}</Text>
      <Text style={styles.colSmall}>{safeCount(row?.female_basins)}</Text>
    </View>
  );
};

const SummaryBlock = ({ label, value }) => (
  <View style={styles.summaryBlock}>
    <Text style={styles.summaryLabel}>{label}</Text>
    <Text style={styles.summaryValue}>{value}</Text>
  </View>
);

const SummaryPage = ({ title, row, floorLabel, includeAccessible }) => {
  const accessible = includeAccessible ? accessibleCount(row) : null;

  return (
    <View style={styles.section}>
      <Text style={styles.sectionTitle}>{title}</Text>
      {!row ? (
        <Text style={styles.muted}>No data available.</Text>
      ) : (
        <View style={styles.summaryGrid}>
          <SummaryBlock label="Floor" value={floorLabel} />
          <SummaryBlock label="NIA" value={`${formatNumber(row.nia_m2, 0)} m²`} />
          <SummaryBlock label="Occupancy" value={safeCount(row.persons)} />
          <SummaryBlock label="Male WCs" value={safeCount(row.male_wcs)} />
          <SummaryBlock label="Female WCs" value={safeCount(row.female_wcs)} />
          {includeAccessible && <SummaryBlock label="Accessible WCs" value={accessible} />}
          <SummaryBlock label="Male basins" value={safeCount(row.male_basins)} />
          <SummaryBlock label="Female basins" value={safeCount(row.female_basins)} />
          {Number.isFinite(row?.male_area_m2) && (
            <SummaryBlock label="Male footprint" value={`${formatNumber(row.male_area_m2, 2)} m²`} />
          )}
          {Number.isFinite(row?.female_area_m2) && (
            <SummaryBlock label="Female footprint" value={`${formatNumber(row.female_area_m2, 2)} m²`} />
          )}
        </View>
      )}
    </View>
  );
};

const WCPDFDocument = ({ project, results, selfContainedRows, includeAccessible, entranceIdx }) => {
  const cubicleRows = Array.isArray(results?.results) ? results.results : [];
  const includeUrinals = results?.provision_mode === "cubicles_urinals";

  const { row: largestCubicle, idx: largestIdx } = findLargestByArea(cubicleRows);
  const largestFloorLabel = largestIdx >= 0 ? storeyName(largestIdx, entranceIdx) : "—";
  const largestSelfContained =
    includeAccessible && Array.isArray(selfContainedRows) && largestIdx >= 0
      ? selfContainedRows[largestIdx]
      : Array.isArray(selfContainedRows) && largestIdx >= 0
        ? selfContainedRows[largestIdx]
        : null;

  return (
    <Document>
      <Page size="A3" orientation="landscape" style={styles.page}>
        <MetadataHeader project={project} />
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Cubicle provision</Text>
          <TableHeader includeUrinals={includeUrinals} includeAccessible={includeAccessible} />
          {cubicleRows.length === 0 ? (
            <Text style={[styles.muted, { marginTop: 8 }]}>No results available.</Text>
          ) : (
            cubicleRows.map((row, idx) => (
              <TableRow
                // eslint-disable-next-line react/no-array-index-key
                key={`cubicle-${idx}`}
                row={row}
                floorLabel={storeyName(row?.floor_index ?? idx, entranceIdx)}
                includeUrinals={includeUrinals}
                includeAccessible={includeAccessible}
              />
            ))
          )}
        </View>
        <View style={styles.footer} fixed>
          <Text>Generated by Pebbledash</Text>
          <Text render={({ pageNumber, totalPages }) => `Page ${pageNumber} / ${totalPages}`} />
        </View>
      </Page>

      <Page size="A3" orientation="landscape" style={styles.page}>
        <MetadataHeader project={project} />
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Self contained provision</Text>
          <TableHeader includeUrinals={false} includeAccessible={includeAccessible} />
          {!Array.isArray(selfContainedRows) || selfContainedRows.length === 0 ? (
            <Text style={[styles.muted, { marginTop: 8 }]}>No results available.</Text>
          ) : (
            selfContainedRows.map((row, idx) => (
              <TableRow
                // eslint-disable-next-line react/no-array-index-key
                key={`self-contained-${idx}`}
                row={row}
                floorLabel={storeyName(row?.floor_index ?? idx, entranceIdx)}
                includeUrinals={false}
                includeAccessible={includeAccessible}
              />
            ))
          )}
        </View>
        <View style={styles.footer} fixed>
          <Text>Generated by Pebbledash</Text>
          <Text render={({ pageNumber, totalPages }) => `Page ${pageNumber} / ${totalPages}`} />
        </View>
      </Page>

      <Page size="A3" orientation="landscape" style={styles.page}>
        <MetadataHeader project={project} />
        <SummaryPage
          title="Largest floor summary — cubicles"
          row={largestCubicle}
          floorLabel={largestFloorLabel}
          includeAccessible={includeAccessible}
        />
        <View style={styles.footer} fixed>
          <Text>Generated by Pebbledash</Text>
          <Text render={({ pageNumber, totalPages }) => `Page ${pageNumber} / ${totalPages}`} />
        </View>
      </Page>

      <Page size="A3" orientation="landscape" style={styles.page}>
        <MetadataHeader project={project} />
        <SummaryPage
          title="Largest floor summary — self contained"
          row={largestSelfContained}
          floorLabel={largestFloorLabel}
          includeAccessible={includeAccessible}
        />
        <View style={styles.footer} fixed>
          <Text>Generated by Pebbledash</Text>
          <Text render={({ pageNumber, totalPages }) => `Page ${pageNumber} / ${totalPages}`} />
        </View>
      </Page>
    </Document>
  );
};

export default WCPDFDocument;
